import { getPageIdentityAliases, resolveFormSchemaIdFromPageEntry } from './resolvePageIdentity'

export interface PageStoreLookupOptions {
  /** Как в GlobalProvider PageDataKey без FormProvider: route.params.id */
  routeParamId?: string | number | null
  /** Текущая открытая страница в store */
  currentPageId?: string | number | null
  /** slotId FormProvider / pageDataSlot */
  slotId?: string | number | null
  /** runtime form id для реестра / действий */
  registryFormId?: string | number | null
  /** window/dialog instance id */
  windowInstanceId?: string | number | null
  /** id верхнего диалога (appStore.dialogIds.at(-1)) */
  topDialogId?: string | number | null
  /** для обратной совместимости */
  formId?: string | number | null
}

const tryPageStoreKey = (
  pagesData: Record<string, any>,
  key: string | number | undefined | null,
): { storeKey: string; page: any } | null => {
  if (key == null || key === '') return null
  const s = String(key)
  if (Object.prototype.hasOwnProperty.call(pagesData, s) && pagesData[s]) {
    return { storeKey: s, page: pagesData[s] }
  }
  const n = typeof key === 'number' ? key : parseInt(s, 10)
  if (Number.isFinite(n)) {
    const ns = String(n)
    if (Object.prototype.hasOwnProperty.call(pagesData, ns) && pagesData[ns]) {
      return { storeKey: ns, page: pagesData[ns] }
    }
  }
  return null
}

const pageAliasesMatchWanted = (
  page: Record<string, any>,
  storeKey: string,
  wanted: string[],
): boolean => {
  if (wanted.length === 0) return false
  const aliases = getPageIdentityAliases(page, { slotId: storeKey })
  return aliases.some((alias) => wanted.includes(alias))
}

const preferOwnFormPageOverSiblingSlot = (
  pagesData: Record<string, any>,
  registryId: string,
  pageDataSlot: string,
  options: {
    topDialogId?: string | number | null
    windowInstanceId?: string | number | null
  } = {},
): { storeKey: string; page: any } | null => {
  if (!registryId || !pageDataSlot || pageDataSlot === registryId) return null

  const topDialogId = String(options.topDialogId ?? '').trim()
  const windowInstanceId = String(options.windowInstanceId ?? '').trim()
  if (topDialogId && topDialogId === pageDataSlot) return null
  if (windowInstanceId && windowInstanceId === pageDataSlot) return null

  const own = tryPageStoreKey(pagesData, registryId)
  const injected = tryPageStoreKey(pagesData, pageDataSlot)
  if (!own || !injected || own.page === injected.page) return null

  const ownSchema = resolveFormSchemaIdFromPageEntry(own.page, registryId)
  if (ownSchema > 0 && String(ownSchema) !== registryId) return null

  const injectedSchema = resolveFormSchemaIdFromPageEntry(injected.page, pageDataSlot)
  if (injectedSchema > 0 && String(injectedSchema) === registryId) {
    return null
  }

  return own
}

const pickBestAliasStoreKey = (
  pagesData: Record<string, any>,
  candidates: string[],
  registryFormId: string,
  preferredKeys: string[],
): string | null => {
  const valid = candidates.filter((key) => pagesData[key])
  if (valid.length === 0) return null
  if (valid.length === 1) return valid[0]

  for (const preferred of preferredKeys) {
    if (preferred && valid.includes(preferred)) return preferred
  }

  const nonRegistry = valid.find((key) => key !== registryFormId)
  return nonRegistry ?? valid[0]
}

/**
 * Найти запись в appStore.pagesData для патча полей (onReloaded и т.п.)
 */
export function findPageStoreEntryForForm(
  pagesData: Record<string, any> | null | undefined,
  options: PageStoreLookupOptions = {},
): { storeKey: string; page: any } | null {
  if (!pagesData || typeof pagesData !== 'object') return null

  const {
    routeParamId,
    currentPageId,
    slotId,
    registryFormId,
    windowInstanceId,
    topDialogId,
    formId,
  } = options

  const registryId = String(registryFormId ?? formId ?? '').trim()
  const slotIdStr = slotId != null && slotId !== '' ? String(slotId) : ''
  const slotIdIsRegistry = Boolean(slotIdStr && registryId && slotIdStr === registryId)
  const wantedIds = [registryId, String(formId ?? '')].filter(Boolean)

  const ownOverSibling = preferOwnFormPageOverSiblingSlot(pagesData, registryId, slotIdStr, {
    topDialogId,
    windowInstanceId,
  })
  if (ownOverSibling) return ownOverSibling

  // pageDataSlot родителя/соседа — только если schema/aliases совпадают (SAME_WINDOW).
  if (slotIdStr && !slotIdIsRegistry) {
    const hit = tryPageStoreKey(pagesData, slotId)
    if (hit && (!registryId || pageAliasesMatchWanted(hit.page, hit.storeKey, wantedIds))) {
      return hit
    }
  }

  if (windowInstanceId != null && windowInstanceId !== '') {
    const hit = tryPageStoreKey(pagesData, windowInstanceId)
    if (hit && (!registryId || pageAliasesMatchWanted(hit.page, hit.storeKey, wantedIds))) {
      return hit
    }
  }

  if (topDialogId != null && topDialogId !== '') {
    const hit = tryPageStoreKey(pagesData, topDialogId)
    if (hit && (!registryId || pageAliasesMatchWanted(hit.page, hit.storeKey, wantedIds))) {
      return hit
    }
  }

  const routeId = routeParamId != null && routeParamId !== '' ? String(routeParamId) : ''
  if (routeId) {
    const hit = tryPageStoreKey(pagesData, routeId)
    if (hit && (!registryId || pageAliasesMatchWanted(hit.page, hit.storeKey, wantedIds))) {
      return hit
    }
  }

  // Explicit FormProvider slot (даже когда slot === registryFormId) — раньше currentPageId,
  // иначе визард/соседняя вкладка (2673) перехватывает action формы 2675.
  if (slotIdStr) {
    const hit = tryPageStoreKey(pagesData, slotId)
    if (hit) {
      if (
        !registryId ||
        slotIdStr === registryId ||
        pageAliasesMatchWanted(hit.page, hit.storeKey, [registryId, String(formId ?? '')])
      ) {
        return hit
      }
    }
  }

  if (currentPageId != null && currentPageId !== '') {
    const cur = String(currentPageId)
    const hit = tryPageStoreKey(pagesData, currentPageId)
    if (hit) {
      if (!registryId || pageAliasesMatchWanted(hit.page, hit.storeKey, [registryId, String(formId ?? '')])) {
        return hit
      }
    }
  }

  const aliasCandidates: string[] = []
  for (const [storeKey, page] of Object.entries(pagesData)) {
    if (!page || typeof page !== 'object') continue
    if (!registryId) continue
    if (pageAliasesMatchWanted(page as Record<string, any>, storeKey, [registryId, String(formId ?? '')])) {
      aliasCandidates.push(storeKey)
    }
  }

  const preferredKeys = [
    slotId != null ? String(slotId) : '',
    windowInstanceId != null ? String(windowInstanceId) : '',
    topDialogId != null ? String(topDialogId) : '',
    routeId,
    currentPageId != null ? String(currentPageId) : '',
  ].filter(Boolean)

  const aliasPick = pickBestAliasStoreKey(pagesData, aliasCandidates, registryId, preferredKeys)
  if (aliasPick) {
    return { storeKey: aliasPick, page: pagesData[aliasPick] }
  }

  if (slotIdStr && slotIdIsRegistry) {
    const hit = tryPageStoreKey(pagesData, slotId)
    if (hit) return hit
  }

  if (registryId) {
    const hit = tryPageStoreKey(pagesData, registryId)
    if (hit) return hit
  }

  return null
}

export interface ResolvePagesDataStoreKeyOptions extends PageStoreLookupOptions {
  pageDataSlot?: string | number | null
  pageDataRef?: unknown
}

const preferredHintsFromOptions = (options: ResolvePagesDataStoreKeyOptions, pageDataSlot: string): string[] =>
  [
    pageDataSlot,
    options.windowInstanceId != null ? String(options.windowInstanceId) : '',
    options.topDialogId != null ? String(options.topDialogId) : '',
    options.currentPageId != null ? String(options.currentPageId) : '',
    options.routeParamId != null ? String(options.routeParamId) : '',
  ].filter(Boolean)

const resolveStoreKeyFromPageDataRef = (
  pagesData: Record<string, any>,
  ref: unknown,
  registryId: string,
  preferredKeys: string[],
): string | null => {
  if (!ref || typeof ref !== 'object') return null
  const matchingKeys = Object.keys(pagesData).filter((key) => pagesData[key] === ref)
  if (matchingKeys.length === 0) return null

  const registryMatched = registryId
    ? matchingKeys.filter((key) =>
        pageAliasesMatchWanted(pagesData[key] as Record<string, any>, key, [registryId]),
      )
    : matchingKeys
  if (registryMatched.length === 0) return null
  if (registryMatched.length === 1) return registryMatched[0]
  return pickBestAliasStoreKey(pagesData, registryMatched, registryId, preferredKeys)
}

/** Ключ pagesData: слот диалога или id реестра формы. */
export function resolvePagesDataStoreKey(
  pagesData: Record<string, any> | null | undefined,
  options: ResolvePagesDataStoreKeyOptions = {},
): string {
  if (!pagesData || typeof pagesData !== 'object') {
    return String(options.pageDataSlot ?? options.formId ?? options.registryFormId ?? '').trim()
  }

  const pageDataSlot = String(options.pageDataSlot ?? options.slotId ?? '').trim()
  const registryId = String(options.registryFormId ?? options.formId ?? '').trim()
  const preferredKeys = preferredHintsFromOptions(options, pageDataSlot)
  const injectedSlotIsRegistry = Boolean(pageDataSlot && registryId && pageDataSlot === registryId)

  const ownOverSibling = preferOwnFormPageOverSiblingSlot(pagesData, registryId, pageDataSlot, {
    topDialogId: options.topDialogId,
    windowInstanceId: options.windowInstanceId,
  })
  if (ownOverSibling) return ownOverSibling.storeKey

  const refKey = resolveStoreKeyFromPageDataRef(
    pagesData,
    options.pageDataRef,
    registryId,
    preferredKeys,
  )
  if (refKey) return refKey

  // pageDataSlot без совпадения formId/aliases — не перехватывать другую форму.
  if (pageDataSlot && !injectedSlotIsRegistry) {
    const hit = tryPageStoreKey(pagesData, pageDataSlot)
    if (
      hit &&
      (!registryId ||
        pageAliasesMatchWanted(hit.page, hit.storeKey, [registryId, String(options.formId ?? '')].filter(Boolean)))
    ) {
      return hit.storeKey
    }
  }

  const entry = findPageStoreEntryForForm(pagesData, {
    routeParamId: options.routeParamId,
    currentPageId: options.currentPageId,
    slotId: options.pageDataSlot ?? options.slotId,
    registryFormId: options.registryFormId ?? options.formId,
    windowInstanceId: options.windowInstanceId,
    topDialogId: options.topDialogId,
    formId: options.formId,
  })
  if (entry) return entry.storeKey

  if (pageDataSlot) {
    const hit = tryPageStoreKey(pagesData, pageDataSlot)
    if (
      hit &&
      (!registryId ||
        pageAliasesMatchWanted(hit.page, hit.storeKey, [registryId, String(options.formId ?? '')].filter(Boolean)))
    ) {
      return hit.storeKey
    }
  }

  if (registryId) {
    const byRegistry = tryPageStoreKey(pagesData, registryId)
    if (byRegistry) return byRegistry.storeKey
  }

  return pageDataSlot || registryId
}

export interface ResolveStateUpdateStoreKeyOptions extends ResolvePagesDataStoreKeyOptions {
  /** Слот FormProvider / pageDataSlot, из которого инициировано обновление */
  originSlotId?: string | number | null
  /** schema id из ответа сервера (form_id / form._attributes.id) */
  responseSchemaFormId?: string | number | null
}

const normalizeSchemaId = (value: unknown): string => {
  if (value == null) return ''
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return String(value)
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed || trimmed === '0') return ''
    const parsed = parseInt(trimmed, 10)
    if (Number.isFinite(parsed) && parsed > 0) return String(parsed)
  }
  return ''
}

/** schema id из ответа action / onChange */
export function resolveResponseSchemaFormId(response: Record<string, any> | null | undefined): string {
  if (!response || typeof response !== 'object') return ''
  const jsResult = response.jsResult as Record<string, unknown> | undefined
  const formRoot = response.form as Record<string, any> | undefined
  const formInner = formRoot?.form as Record<string, any> | undefined
  const formAttrs = (formInner?._attributes ?? formRoot?._attributes) as Record<string, unknown> | undefined

  for (const candidate of [
    jsResult?.form_id,
    formAttrs?.id,
    response.status,
    formAttrs?.fid,
  ]) {
    const normalized = normalizeSchemaId(candidate)
    if (normalized) return normalized
  }
  return ''
}

/**
 * Один ключ pagesData для записи states/changed_data — без fan-out по alias.
 * Приоритет originSlotId; при несовпадении schema id ответа и layout слота — resolvePagesDataStoreKey.
 */
export function resolveStateUpdateStoreKey(
  pagesData: Record<string, any> | null | undefined,
  options: ResolveStateUpdateStoreKeyOptions = {},
): string {
  const originSlotId = String(
    options.originSlotId ?? options.pageDataSlot ?? options.slotId ?? '',
  ).trim()
  const responseSchemaId = normalizeSchemaId(options.responseSchemaFormId)

  if (originSlotId && pagesData?.[originSlotId]) {
    if (responseSchemaId) {
      const slotSchemaId = String(
        resolveFormSchemaIdFromPageEntry(pagesData[originSlotId], originSlotId) || '',
      )
      if (
        slotSchemaId &&
        slotSchemaId !== '0' &&
        responseSchemaId !== slotSchemaId &&
        responseSchemaId !== originSlotId
      ) {
        // layout слота не соответствует ответу — не пишем в origin
      } else {
        return originSlotId
      }
    } else {
      return originSlotId
    }
  }

  const registryId = String(options.registryFormId ?? options.formId ?? '').trim()
  if (registryId && pagesData?.[registryId]) {
    if (!responseSchemaId || responseSchemaId === registryId) {
      return registryId
    }
    const registrySchemaId = String(
      resolveFormSchemaIdFromPageEntry(pagesData[registryId], registryId) || '',
    )
    if (!registrySchemaId || registrySchemaId === responseSchemaId) {
      return registryId
    }
  }

  return resolvePagesDataStoreKey(pagesData, options)
}
